import { useState } from "react";
import "./Sample.scss";
import { useNavigate } from "react-router-dom";
import { toggleFavorite } from "../lib/api";
import { trackSampleFileClick } from "../analytics";
import { useAuth } from "../context/AuthContext";
import { useSignInModal } from "../context/SignInModalContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { recentAnalysesOptions, analysisKeys } from "../lib/queries";
import AnalysisList from "./AnalysisList";

export default function Sample() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const { openSignInModal } = useSignInModal();
  const [favoriteLoading, setFavoriteLoading] = useState<Set<string>>(
    new Set(),
  );

  const {
    data: samples = [],
    isLoading: loading,
    isError,
  } = useQuery(recentAnalysesOptions(10));

  const favoriteMutation = useMutation({
    mutationFn: ({
      analysisId,
      isFavorite,
    }: {
      analysisId: string;
      isFavorite: boolean;
    }) => toggleFavorite(analysisId, isFavorite),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: analysisKeys.all });
    },
  });

  const handleSampleClick = (analysisId: string) => {
    trackSampleFileClick(analysisId);
    navigate(`/analysis/${analysisId}`);
  };

  const handleFavoriteToggle = async (
    e: React.MouseEvent,
    analysisId: string,
  ) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      openSignInModal();
      return;
    }

    if (favoriteLoading.has(analysisId)) return;

    const item = samples.find((s) => s.analysisId === analysisId);
    const newIsFavorite = !item?.isFavorite;
    setFavoriteLoading((prev) => new Set(prev).add(analysisId));

    try {
      await favoriteMutation.mutateAsync({ analysisId, isFavorite: newIsFavorite });
    } catch (err) {
      console.error("Failed to toggle favorite:", err);
    } finally {
      setFavoriteLoading((prev) => {
        const next = new Set(prev);
        next.delete(analysisId);
        return next;
      });
    }
  };

  if (isError) {
    return (
      <div className="sample-container">
        <div className="error-message">
          Failed to load recent scans. Please try again later.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="sample-container">
        <div className="loading-container">
          <p>Loading recent scans...</p>
        </div>
      </div>
    );
  }

  if (samples.length === 0) {
    return (
      <div className="sample-container">
        <div className="no-scans">
          <p>No scans found yet. Upload a file to get started!</p>
        </div>
      </div>
    );
  }

  return (
    <AnalysisList
      items={samples}
      onFavoriteToggle={handleFavoriteToggle}
      onItemClick={handleSampleClick}
    />
  );
}
